import React, { useEffect, useRef, useState } from "react";
import { Check, ChevronDown, Copy, ExternalLink, LogOut } from "lucide-react";
import { getExplorerBaseUrlForChain } from "@/chains";
import { truncateAddress } from "@/lib/blockscout";

interface WalletMenuProps {
  address: string;
  chainId?: number | null;
  onDisconnect: () => void;
  menuAlign?: "left" | "right";
}

function addressAccent(address: string): { from: string; to: string } {
  const hex = address.replace(/^0x/i, "").slice(0, 6);
  const parsed = parseInt(hex, 16);
  const hue = Number.isFinite(parsed) ? parsed % 360 : 210;
  return {
    from: `hsl(${hue} 72% 52%)`,
    to: `hsl(${(hue + 48) % 360} 78% 58%)`,
  };
}

/** Split a 0x-address into two equal mono lines (0x + 20 hex / 20 hex). */
function formatAddressLines(address: string): [string, string] {
  const normalized = address.trim();
  if (/^0x[a-fA-F0-9]{40}$/.test(normalized)) {
    return [normalized.slice(0, 22), normalized.slice(22)];
  }
  const mid = Math.ceil(normalized.length / 2);
  return [normalized.slice(0, mid), normalized.slice(mid)];
}

const WalletMenu: React.FC<WalletMenuProps> = ({
  address,
  chainId,
  onDisconnect,
  menuAlign = "right",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const accent = addressAccent(address);
  const short = truncateAddress(address);
  const [addressLine1, addressLine2] = formatAddressLines(address);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  useEffect(() => {
    if (!copied) return;
    const t = window.setTimeout(() => setCopied(false), 1600);
    return () => window.clearTimeout(t);
  }, [copied]);

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setIsOpen(false);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isOpen]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(address);
      setCopied(true);
    } catch {
      // non-fatal
    }
  };

  const handleExplorer = () => {
    if (!chainId) return;
    const base = getExplorerBaseUrlForChain(chainId).replace(/\/$/, "");
    window.open(`${base}/address/${address}`, "_blank", "noopener,noreferrer");
    setIsOpen(false);
  };

  const handleDisconnect = () => {
    setIsOpen(false);
    onDisconnect();
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen((o) => !o)}
        className="flex h-10 items-center gap-2 rounded-full bg-foreground/5 pl-1.5 pr-3 text-xs md:text-sm font-medium text-foreground ring-1 ring-border shadow-sm transition-all duration-200 hover:bg-foreground/10 dark:bg-white/10 dark:hover:bg-white/15"
        aria-label={`Wallet ${short}`}
        aria-expanded={isOpen}
        aria-haspopup="menu"
      >
        <span
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full ring-1 ring-white/20"
          style={{ background: `linear-gradient(135deg, ${accent.from}, ${accent.to})` }}
          aria-hidden
        />
        <span className="font-mono text-[11px] md:text-xs tracking-tight">{short}</span>
        <ChevronDown
          className={`h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
        />
      </button>

      {isOpen && (
        <div
          role="menu"
          aria-label="Wallet menu"
          className={`absolute top-full z-50 mt-2 w-[min(16.5rem,calc(100vw-2rem))] overflow-hidden rounded-2xl dropdown-panel shadow-2xl backdrop-blur-md
                     ${menuAlign === "right" ? "right-0" : "left-0"}`}
        >
          <div className="border-b border-border/60 px-3.5 py-3">
            <div className="flex items-center gap-2.5">
              <span
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full ring-1 ring-white/20"
                style={{ background: `linear-gradient(135deg, ${accent.from}, ${accent.to})` }}
                aria-hidden
              />
              <div className="min-w-0 flex-1">
                <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                  Account
                </p>
                <p
                  className="mt-1 font-mono text-[11px] leading-[1.45] tracking-wide text-foreground/90"
                  title={address}
                >
                  <span className="block">{addressLine1}</span>
                  {addressLine2 ? <span className="block">{addressLine2}</span> : null}
                </p>
              </div>
            </div>
          </div>

          <div className="p-1.5">
            <button
              type="button"
              role="menuitem"
              onClick={handleCopy}
              className="flex w-full items-center gap-2.5 rounded-xl px-2.5 py-2.5 text-sm text-foreground transition-colors hover:bg-muted/80"
            >
              {copied ? (
                <Check className="h-4 w-4 shrink-0 text-emerald-400" />
              ) : (
                <Copy className="h-4 w-4 shrink-0 text-muted-foreground" />
              )}
              <span>{copied ? "Copied" : "Copy address"}</span>
            </button>

            <button
              type="button"
              role="menuitem"
              onClick={handleExplorer}
              disabled={!chainId}
              className="flex w-full items-center gap-2.5 rounded-xl px-2.5 py-2.5 text-sm text-foreground transition-colors hover:bg-muted/80 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <ExternalLink className="h-4 w-4 shrink-0 text-muted-foreground" />
              <span>View on explorer</span>
            </button>

            <div className="my-1 h-px bg-border/60" role="separator" />

            <button
              type="button"
              role="menuitem"
              onClick={handleDisconnect}
              className="flex w-full items-center gap-2.5 rounded-xl px-2.5 py-2.5 text-sm text-red-400 transition-colors hover:bg-red-500/10 hover:text-red-300"
            >
              <LogOut className="h-4 w-4 shrink-0" />
              <span>Disconnect</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default WalletMenu;
